/**
 * @vitest-environment jsdom
 */

import { describe, expect, afterEach, it, vi } from "vitest";
import { isbot } from "isbot";

import * as mixpanel from "./mixpanel";
import * as posthog from "./posthog";
import { isLikelyAutomated } from "./bot-detection";
import { InsightsService } from "./service";
import { InsightsCommandQueue } from "./command-queue";

// Real implementation by default; individual tests can override to simulate
// library failure
vi.mock("isbot", async (importOriginal) => {
  const actual = await importOriginal<typeof import("isbot")>();
  return { ...actual, isbot: vi.fn(actual.isbot) };
});

vi.mock("./mixpanel", () => ({
  initMixpanel: vi.fn(),
  trackPageView: vi.fn(),
  track: vi.fn(),
}));

vi.mock("./posthog", () => ({
  initPosthog: vi.fn(),
  trackPageView: vi.fn(),
  track: vi.fn(),
}));

vi.mock("./datalayer", () => ({
  track: vi.fn(),
  trackPageView: vi.fn(),
}));

vi.mock("./logger", () => ({
  debug: vi.fn(),
  info: vi.fn(),
  warn: vi.fn(),
  error: vi.fn(),
}));

const CHROME_UA =
  "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36";

const stubNavigator = (overrides: {
  webdriver?: boolean;
  userAgent?: string;
}) => {
  vi.stubGlobal("navigator", {
    webdriver: false,
    userAgent: CHROME_UA,
    ...overrides,
  });
};

describe("isLikelyAutomated", () => {
  afterEach(() => {
    vi.unstubAllGlobals();
  });

  it("passes a regular Chrome browser", () => {
    stubNavigator({});
    expect(isLikelyAutomated()).toBe(false);
  });

  it("passes real Firefox", () => {
    stubNavigator({
      userAgent:
        "Mozilla/5.0 (Macintosh; Intel Mac OS X 10.15; rv:130.0) Gecko/20100101 Firefox/130.0",
    });
    expect(isLikelyAutomated()).toBe(false);
  });

  it("passes real Safari despite its Version/x token", () => {
    stubNavigator({
      userAgent:
        "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.0 Safari/605.1.15",
    });
    expect(isLikelyAutomated()).toBe(false);
  });

  it("flags webdriver automation regardless of user agent", () => {
    stubNavigator({ webdriver: true });
    expect(isLikelyAutomated()).toBe(true);
  });

  it("flags headless browsers", () => {
    stubNavigator({
      userAgent:
        "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) HeadlessChrome/147.0.7727.15 Safari/537.36",
    });
    expect(isLikelyAutomated()).toBe(true);
  });

  it("flags self-identified crawlers", () => {
    stubNavigator({
      userAgent:
        "Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko); compatible; GPTBot/1.0; +https://openai.com/gptbot",
    });
    expect(isLikelyAutomated()).toBe(true);
  });

  it("treats environments without navigator as automated", () => {
    vi.stubGlobal("navigator", undefined);
    expect(isLikelyAutomated()).toBe(true);
  });

  // Verbatim UA of the scraper fleet observed hammering ably.com — isbot
  // cannot catch it (browser-shaped), so the forged-UA heuristic must
  // catch it instead
  it("flags forged Safari/Firefox hybrid user agents", () => {
    stubNavigator({
      userAgent:
        "Mozilla/5.0 (Macintosh; Intel Mac OS X 12) AppleWebKit/601.3.9 (KHTML, like Gecko) Version/9.0.2 Firefox/99.0",
    });
    expect(isLikelyAutomated()).toBe(true);
  });

  it("flags SEO/monitoring crawlers via isbot", () => {
    stubNavigator({
      userAgent:
        "Mozilla/5.0 (compatible; ContentKing/1.0; +https://www.contentkingapp.com)",
    });
    expect(isLikelyAutomated()).toBe(true);
  });

  it("flags self-declared indexing crawlers via isbot", () => {
    stubNavigator({
      userAgent:
        "ExoPriorsSiteClosure/1.0 (content indexing; contact@exopriors.com)",
    });
    expect(isLikelyAutomated()).toBe(true);
  });

  it("flags LG webOS smart TVs — a known, accepted isbot false positive", () => {
    stubNavigator({
      userAgent:
        "Mozilla/5.0 (Web0S; Linux/SmartTV) AppleWebKit/537.36 (KHTML, like Gecko) 85.0.4183.93/STR Large Screen WebAppManager Safari/537.36",
    });
    expect(isLikelyAutomated()).toBe(true);
  });

  it("fails closed when bot detection itself throws", () => {
    stubNavigator({});
    vi.mocked(isbot).mockImplementationOnce(() => {
      throw new Error("pattern explosion");
    });
    expect(isLikelyAutomated()).toBe(true);
  });
});

describe("InsightsService bot gating", () => {
  afterEach(() => {
    vi.unstubAllGlobals();
    vi.clearAllMocks();
  });

  const config = {
    mixpanelToken: "test-token",
    mixpanelAutoCapture: true,
    posthogApiKey: "test-key",
    posthogApiHost: "test-host",
  };

  it("skips all analytics initialization for automated clients", () => {
    stubNavigator({ webdriver: true });

    new InsightsService().initInsights(config);

    expect(mixpanel.initMixpanel).not.toHaveBeenCalled();
    expect(posthog.initPosthog).not.toHaveBeenCalled();
  });

  it("initializes analytics for regular browsers", () => {
    stubNavigator({});

    new InsightsService().initInsights(config);

    expect(mixpanel.initMixpanel).toHaveBeenCalled();
    expect(posthog.initPosthog).toHaveBeenCalled();
  });
});

describe("InsightsCommandQueue bot gating", () => {
  afterEach(() => {
    vi.unstubAllGlobals();
    vi.clearAllMocks();
  });

  const config = {
    mixpanelToken: "test-token",
    mixpanelAutoCapture: true,
    posthogApiKey: "test-key",
    posthogApiHost: "test-host",
  };

  // Pins the guarantee that gated clients can't reach the SDKs through
  // calls made after init (route-change page views etc.) — we must not
  // depend on Mixpanel/PostHog's own pre-init guards for that
  it("keeps post-init calls away from the SDKs for automated clients", () => {
    stubNavigator({ webdriver: true });

    const queue = new InsightsCommandQueue();
    queue.initInsights(config);
    queue.trackPageView();
    queue.track("some_event", {});

    expect(mixpanel.initMixpanel).not.toHaveBeenCalled();
    expect(mixpanel.trackPageView).not.toHaveBeenCalled();
    expect(mixpanel.track).not.toHaveBeenCalled();
    expect(posthog.initPosthog).not.toHaveBeenCalled();
    expect(posthog.trackPageView).not.toHaveBeenCalled();
  });

  it("dispatches post-init calls normally for regular browsers", () => {
    stubNavigator({});

    const queue = new InsightsCommandQueue();
    queue.initInsights(config);
    queue.trackPageView();

    expect(mixpanel.initMixpanel).toHaveBeenCalled();
    expect(mixpanel.trackPageView).toHaveBeenCalled();
  });
});
